"use client";

import { useAuth } from "@/components/AuthContext";
import { CinematicModal } from "@/components/ui/CinematicModal";
import {
  FREE_VOICES,
  PREMIUM_VOICES,
  VOICE_DESCRIPTIONS,
  VOICE_DISPLAY_NAMES,
  VoiceKey,
} from "@/data/voiceDisplayNames";
import { cinematicModalCloseButtonClass } from "@/lib/cinematicModalStyles";
import { TUTOR_BUILDER_LIMITS } from "@/lib/school/tutorBuilderContract";
import { supabase } from "@/lib/supabaseClient";
import {
  GeneratedTutorDraft,
  TutorArchetype,
  TutorCurriculumDraftItem,
  TutorExperienceType,
} from "@/types/tutor";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { AnimatePresence, motion } from "framer-motion";
import {
  BookOpenCheck,
  Brain,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Crown,
  Eye,
  MessageCircle,
  PencilLine,
  Play,
  RefreshCw,
  Route,
  Save,
  SlidersHorizontal,
  Sparkles,
  Target,
  X,
} from "lucide-react";
import React, { useMemo, useState } from "react";

interface CreateTutorModalProps {
  onClose: () => void;
  onCreated: () => void;
}

type BuilderStep = "Goal" | "Persona" | "Session" | "Preview";

type BuilderState = {
  goal: string;
  audience: string;
  sourceText: string;
  category: string;
  archetype: TutorArchetype;
  tone: string;
  strictness: "gentle" | "balanced" | "strict";
  correctionStyle: string;
  sessionStyle: string;
  greeting: string;
  experienceType: TutorExperienceType;
  voice_id: VoiceKey;
  isPublic: boolean;
  includeJourney: boolean;
  lessonCount: number;
};

const STEPS: BuilderStep[] = ["Goal", "Persona", "Session", "Preview"];

const CATEGORY_OPTIONS = ["Language", "Academic", "Coding", "Roleplay", "Wellness", "General"];

const ARCHETYPE_OPTIONS: Array<{
  value: TutorArchetype;
  label: string;
  description: string;
}> = [
  {
    value: "language_coach",
    label: "Language coach",
    description: "Conversation, correction, pronunciation, and fluency.",
  },
  {
    value: "exam_prep",
    label: "Exam prep",
    description: "Practice loops, diagnostic questions, and mastery checks.",
  },
  {
    value: "coding_mentor",
    label: "Coding mentor",
    description: "Debugging, concepts, exercises, and code review habits.",
  },
  {
    value: "roleplay_coach",
    label: "Roleplay coach",
    description: "Interviews, sales, travel, customer calls, and scenarios.",
  },
  {
    value: "subject_tutor",
    label: "Subject tutor",
    description: "Math, history, science, writing, and hard concepts.",
  },
  {
    value: "wellness_guide",
    label: "Wellness guide",
    description: "Reflection, grounding, and calm practice without therapy claims.",
  },
];

const TONE_OPTIONS = [
  "warm and direct",
  "playful and energetic",
  "calm and precise",
  "Socratic and curious",
];

const SESSION_STYLE_OPTIONS = [
  "guided conversation",
  "diagnose then drill",
  "roleplay with feedback",
  "teach one concept then test it",
];

const BUILDER_FIELD_LABELS: Record<string, string> = {
  goal: "Goal",
  audience: "Audience",
  category: "Category",
  archetype: "Tutor persona",
  tone: "Tone",
  correctionStyle: "Correction behavior",
  sessionStyle: "Teaching loop",
  sourceText: "Curriculum source",
  experienceType: "Session surface",
};

const VOICE_ICON_MAP: Record<VoiceKey, React.ComponentType<{ className?: string }>> = {
  Puck: Sparkles,
  Charon: Brain,
  Kore: MessageCircle,
  Fenrir: Target,
  Aoede: BookOpenCheck,
  Leda: Route,
  Orus: Eye,
  Zephyr: SlidersHorizontal,
};

const DEFAULT_BUILDER: BuilderState = {
  goal: "",
  audience: "",
  sourceText: "",
  category: "Language",
  archetype: "language_coach",
  tone: "warm and direct",
  strictness: "balanced",
  correctionStyle: "correct one important issue at a time, then ask the learner to try again",
  sessionStyle: "guided conversation",
  greeting: "",
  experienceType: "conversation",
  voice_id: "Aoede",
  isPublic: true,
  includeJourney: true,
  lessonCount: 5,
};

function getStepStatus(index: number, currentStep: number) {
  if (index < currentStep) return "complete";
  if (index === currentStep) return "active";
  return "upcoming";
}

function buildDraftRequest(builder: BuilderState) {
  const strictnessText =
    builder.strictness === "gentle"
      ? "gentle, confidence-building, low-pressure"
      : builder.strictness === "strict"
        ? "strict, focused, and high-accountability"
        : "balanced, clear, and supportive";

  return {
    goal: builder.goal.trim(),
    audience: builder.audience.trim(),
    category: builder.category,
    archetype: builder.archetype,
    tone: `${builder.tone}; ${strictnessText}`,
    correctionStyle: builder.correctionStyle.trim(),
    sessionStyle: builder.sessionStyle,
    sourceText: builder.sourceText.trim(),
    experienceType: builder.experienceType,
  };
}

type BuilderApiPayload = {
  error?: string;
  issues?: Array<{ path?: Array<string | number>; message?: string }>;
};

function getBuilderApiError(payload: BuilderApiPayload | null, fallback: string) {
  const issue = payload?.issues?.[0];
  const fieldKey = typeof issue?.path?.[0] === "string" ? issue.path[0] : null;
  const fieldLabel = fieldKey ? BUILDER_FIELD_LABELS[fieldKey] || fieldKey : null;

  if (fieldKey === "sourceText" && issue?.message) {
    return `Curriculum source is too long. Keep it at ${TUTOR_BUILDER_LIMITS.sourceText.toLocaleString()} characters or fewer and try again.`;
  }

  if (fieldLabel && issue?.message) {
    return `${fieldLabel}: ${issue.message}`;
  }

  return payload?.error || fallback;
}

function getCanContinue(
  step: BuilderStep,
  builder: BuilderState,
  draft: GeneratedTutorDraft | null
) {
  if (builder.sourceText.length > TUTOR_BUILDER_LIMITS.sourceText) return false;
  if (step === "Goal") return builder.goal.trim().length >= 8;
  if (step === "Persona") return Boolean(builder.archetype && builder.tone);
  if (step === "Session") return Boolean(builder.correctionStyle && builder.sessionStyle);
  return Boolean(draft);
}

async function getSchoolBuilderHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Please sign in again before creating a tutor.");
  }

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${session.access_token}`,
  };
}

export default function CreateTutorModal({ onClose, onCreated }: CreateTutorModalProps) {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [builder, setBuilder] = useState<BuilderState>(DEFAULT_BUILDER);
  const [draft, setDraft] = useState<GeneratedTutorDraft | null>(null);
  const [journeyItems, setJourneyItems] = useState<TutorCurriculumDraftItem[]>([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [loadingJourney, setLoadingJourney] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const step = STEPS[currentStep];
  const sourceCharacterCount = builder.sourceText.length;
  const sourceCharactersOverLimit = Math.max(
    0,
    sourceCharacterCount - TUTOR_BUILDER_LIMITS.sourceText
  );
  const canContinue = getCanContinue(step, builder, draft);
  const selectedVoice = builder.voice_id;
  const SelectedVoiceIcon = VOICE_ICON_MAP[selectedVoice] || Sparkles;

  const sourceLabel = useMemo(() => {
    if (!sourceCharacterCount)
      return `Optional · up to ${TUTOR_BUILDER_LIMITS.sourceText.toLocaleString()} characters`;
    return `${sourceCharacterCount.toLocaleString()} / ${TUTOR_BUILDER_LIMITS.sourceText.toLocaleString()} characters`;
  }, [sourceCharacterCount]);

  const updateBuilder = (patch: Partial<BuilderState>) => {
    setBuilder((prev) => ({ ...prev, ...patch }));
    setDraft(null);
    setJourneyItems([]);
    setError(null);
  };

  const updateDraft = (patch: Partial<GeneratedTutorDraft>) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const generateDraft = async () => {
    if (!user) return;
    if (sourceCharactersOverLimit > 0) {
      setError(
        `Curriculum source is ${sourceCharactersOverLimit.toLocaleString()} characters over the ${TUTOR_BUILDER_LIMITS.sourceText.toLocaleString()} character limit.`
      );
      return;
    }
    setLoadingDraft(true);
    setError(null);

    try {
      const headers = await getSchoolBuilderHeaders();
      const response = await fetch("/api/school/tutor-drafts", {
        method: "POST",
        headers,
        body: JSON.stringify(buildDraftRequest(builder)),
      });
      const payload = (await response.json().catch(() => null)) as BuilderApiPayload & {
        draft?: GeneratedTutorDraft;
      };
      if (!response.ok || !payload?.draft) {
        throw new Error(getBuilderApiError(payload, "Could not generate a tutor draft."));
      }

      const nextDraft = payload.draft as GeneratedTutorDraft;
      if (builder.greeting.trim()) {
        nextDraft.opening_message = builder.greeting.trim();
      }
      setDraft(nextDraft);
      setJourneyItems(builder.includeJourney ? nextDraft.curriculum_preview || [] : []);
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "We couldn't create the tutor preview. Check your details and try again."
      );
    } finally {
      setLoadingDraft(false);
    }
  };

  const generateJourney = async () => {
    if (!user || !draft) return;
    setLoadingJourney(true);
    setError(null);

    try {
      const headers = await getSchoolBuilderHeaders();
      const response = await fetch("/api/school/tutor-journeys", {
        method: "POST",
        headers,
        body: JSON.stringify({
          tutorName: draft.name,
          tutorGoal: draft.goal,
          archetype: draft.archetype,
          sourceText: builder.sourceText,
          lessonCount: builder.lessonCount,
        }),
      });
      const payload = (await response.json().catch(() => null)) as BuilderApiPayload & {
        items?: TutorCurriculumDraftItem[];
      };
      if (!response.ok || !Array.isArray(payload?.items)) {
        throw new Error(getBuilderApiError(payload, "Could not generate Journey lessons."));
      }
      setJourneyItems(payload.items);
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "We couldn't create the Journey lessons. Please try again."
      );
    } finally {
      setLoadingJourney(false);
    }
  };

  const handleNext = async () => {
    if (step === "Session" && !draft) {
      await generateDraft();
      setCurrentStep(3);
      return;
    }
    if (step === "Preview") {
      await saveTutor();
      return;
    }
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const saveTutor = async () => {
    if (!user || !draft) return;
    setSaving(true);
    setError(null);

    try {
      const headers = await getSchoolBuilderHeaders();
      const response = await fetch("/api/school/tutors", {
        method: "POST",
        headers,
        body: JSON.stringify({
          draft,
          voiceId: builder.voice_id,
          isPublic: builder.isPublic,
          curriculumItems: builder.includeJourney ? journeyItems : [],
        }),
      });
      const payload = (await response.json().catch(() => null)) as BuilderApiPayload;
      if (!response.ok && response.status !== 207) {
        throw new Error(getBuilderApiError(payload, "Could not save this tutor."));
      }
      onCreated();
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "We couldn't save this tutor. Check your connection and try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const renderGoalStep = () => (
    <div className="grid gap-5 lg:grid-cols-[1.05fr_0.95fr]">
      <div className="space-y-5">
        <Field label="Goal" hint="What should this tutor help someone become able to do?">
          <textarea
            value={builder.goal}
            onChange={(event) => updateBuilder({ goal: event.target.value })}
            placeholder="Example: help a Dutch learner handle real customer calls with polite corrections and roleplay."
            className="min-h-36 w-full resize-none rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3 text-sm leading-relaxed text-white outline-none transition focus:border-orange-300/55"
            maxLength={TUTOR_BUILDER_LIMITS.goal}
            autoFocus
          />
        </Field>
        <Field label="Audience" hint="Optional, but useful for tone and difficulty.">
          <input
            value={builder.audience}
            onChange={(event) => updateBuilder({ audience: event.target.value })}
            placeholder="Example: B1 adults, beginner coders, AP history students..."
            className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white outline-none transition focus:border-[#FD954F]/70 focus:ring-2 focus:ring-[#FD954F]/20"
            maxLength={TUTOR_BUILDER_LIMITS.audience}
          />
        </Field>
      </div>

      <div className="space-y-5 lg:border-l lg:border-white/10 lg:pl-5">
        <div>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-white">Curriculum source</div>
              <div
                className={`text-xs ${sourceCharactersOverLimit > 0 ? "text-red-300" : "text-white/45"}`}
              >
                {sourceLabel}
              </div>
            </div>
            <BookOpenCheck className="h-5 w-5 text-[#FD954F]" />
          </div>
          <textarea
            value={builder.sourceText}
            onChange={(event) => updateBuilder({ sourceText: event.target.value })}
            placeholder="Paste notes, an outline, a syllabus, a support script, or a curriculum excerpt. This is optional for v1."
            aria-invalid={sourceCharactersOverLimit > 0}
            aria-describedby={
              sourceCharactersOverLimit > 0 ? "tutor-source-limit-error" : undefined
            }
            className={`min-h-48 w-full resize-none rounded-lg border bg-[#0F0F14] px-4 py-3 text-sm leading-relaxed text-white outline-none transition focus:ring-2 ${
              sourceCharactersOverLimit > 0
                ? "border-red-400/70 focus:border-red-400 focus:ring-red-400/20"
                : "border-white/10 focus:border-[#FD954F]/70 focus:ring-[#FD954F]/20"
            }`}
          />
          {sourceCharactersOverLimit > 0 && (
            <p id="tutor-source-limit-error" className="mt-2 text-xs font-medium text-red-300">
              Remove {sourceCharactersOverLimit.toLocaleString()} characters to continue. The
              curriculum source limit is {TUTOR_BUILDER_LIMITS.sourceText.toLocaleString()}.
            </p>
          )}
        </div>
        <label className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.025] p-4">
          <input
            type="checkbox"
            checked={builder.includeJourney}
            onChange={(event) => updateBuilder({ includeJourney: event.target.checked })}
            className="mt-1 rounded border-white/20 bg-white/10 text-[#FD954F] focus:ring-[#FD954F]"
          />
          <span>
            <span className="block text-sm font-semibold text-white">
              Generate optional Journey
            </span>
            <span className="block text-xs leading-relaxed text-white/50">
              Adds a few editable lesson cards. Freeform tutoring still works without this.
            </span>
          </span>
        </label>
      </div>
    </div>
  );

  const renderPersonaStep = () => (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {ARCHETYPE_OPTIONS.map((option) => {
          const selected = builder.archetype === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => updateBuilder({ archetype: option.value })}
              className={`rounded-lg border p-4 text-left transition ${
                selected
                  ? "border-[#FD954F] bg-[#FD954F]/12 shadow-[0_0_28px_rgba(253,149,79,0.12)]"
                  : "border-white/10 bg-white/[0.035] hover:border-white/25 hover:bg-white/[0.06]"
              }`}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-white">{option.label}</span>
                {selected && <Check className="h-4 w-4 text-[#FD954F]" />}
              </div>
              <p className="text-xs leading-relaxed text-white/50">{option.description}</p>
            </button>
          );
        })}
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <Field label="Category">
          <BuilderSelect
            value={builder.category}
            options={CATEGORY_OPTIONS}
            onValueChange={(category) => updateBuilder({ category })}
            ariaLabel="Choose tutor category"
          />
        </Field>
        <Field label="Tone">
          <BuilderSelect
            value={builder.tone}
            options={TONE_OPTIONS}
            onValueChange={(tone) => updateBuilder({ tone })}
            ariaLabel="Choose tutor tone"
          />
        </Field>
        <Field label="Strictness">
          <div className="grid grid-cols-3 gap-2">
            {(["gentle", "balanced", "strict"] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => updateBuilder({ strictness: value })}
                className={`rounded-lg border px-3 py-3 text-xs font-semibold capitalize transition ${
                  builder.strictness === value
                    ? "border-[#FD954F] bg-[#FD954F] text-[#181A22]"
                    : "border-white/10 bg-white/[0.04] text-white/60 hover:text-white"
                }`}
              >
                {value}
              </button>
            ))}
          </div>
        </Field>
      </div>
    </div>
  );

  const renderSessionStep = () => (
    <div className="grid gap-6 lg:grid-cols-[1fr_0.95fr]">
      <div className="space-y-5">
        <Field label="Teaching loop" hint="The default pattern for every turn.">
          <BuilderSelect
            value={builder.sessionStyle}
            options={SESSION_STYLE_OPTIONS}
            onValueChange={(sessionStyle) => updateBuilder({ sessionStyle })}
            ariaLabel="Choose teaching loop"
          />
        </Field>
        <Field label="Correction behavior">
          <textarea
            value={builder.correctionStyle}
            onChange={(event) => updateBuilder({ correctionStyle: event.target.value })}
            className="min-h-28 w-full resize-none rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3 text-sm leading-relaxed text-white outline-none transition focus:border-[#FD954F]/70 focus:ring-2 focus:ring-[#FD954F]/20"
            maxLength={TUTOR_BUILDER_LIMITS.correctionStyle}
          />
        </Field>
        <Field label="Optional first greeting">
          <input
            value={builder.greeting}
            onChange={(event) => updateBuilder({ greeting: event.target.value })}
            placeholder="Leave blank and ChickyTutor will generate one."
            className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white outline-none transition focus:border-[#FD954F]/70 focus:ring-2 focus:ring-[#FD954F]/20"
            maxLength={TUTOR_BUILDER_LIMITS.greeting}
          />
        </Field>
      </div>

      <div className="space-y-5 lg:border-l lg:border-white/10 lg:pl-5">
        <SegmentedExperience
          value={builder.experienceType}
          onChange={(experienceType) => updateBuilder({ experienceType })}
        />
        <VoicePicker
          selectedVoice={builder.voice_id}
          onSelect={(voiceId) => updateBuilder({ voice_id: voiceId })}
        />
        <label className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.025] p-4">
          <input
            type="checkbox"
            checked={!builder.isPublic}
            onChange={(event) => updateBuilder({ isPublic: !event.target.checked })}
            className="mt-1 rounded border-white/20 bg-white/10 text-[#FD954F] focus:ring-[#FD954F]"
          />
          <span>
            <span className="block text-sm font-semibold text-white">Keep this tutor private</span>
            <span className="block text-xs leading-relaxed text-white/50">
              By default, new tutors are visible to you and submitted for review before public
              listing. Select this if the tutor is only for you.
            </span>
          </span>
        </label>
      </div>
    </div>
  );

  const renderPreviewStep = () => {
    if (!draft) {
      return (
        <div className="flex min-h-80 flex-col items-center justify-center border-y border-white/10 p-8 text-center">
          <Brain className="mb-4 h-10 w-10 text-[#FD954F]" />
          <h3 className="text-xl font-bold text-white">Generate the tutor draft</h3>
          <p className="mt-2 max-w-lg text-sm leading-relaxed text-white/55">
            ChickyTutor will turn your inputs into a distinctive tutor card, first greeting,
            teaching prompt, sample exchange, and optional lesson Journey.
          </p>
          <button
            type="button"
            onClick={generateDraft}
            disabled={loadingDraft || !builder.goal.trim()}
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[#FD954F] px-5 py-3 text-sm font-black text-[#181A22] transition hover:bg-[#ffae73] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loadingDraft ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Generate preview
          </button>
        </div>
      );
    }

    return (
      <div className="grid gap-6 lg:grid-cols-[0.92fr_1.08fr]">
        <div className="space-y-4">
          <div className="rounded-lg border border-white/10 bg-[#15161B] p-5">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <div className="mb-2 inline-flex items-center gap-2 text-xs font-bold text-[#FFD0B1]">
                  {draft.category}
                </div>
                <input
                  value={draft.name}
                  onChange={(event) => updateDraft({ name: event.target.value })}
                  className="w-full bg-transparent text-2xl font-black text-white outline-none"
                />
              </div>
              <div className="rounded-lg bg-[#FD954F] px-3 py-2 text-sm font-black text-[#181A22]">
                {Math.round(draft.quality_score)}%
              </div>
            </div>
            <textarea
              value={draft.description}
              onChange={(event) => updateDraft({ description: event.target.value })}
              className="min-h-20 w-full resize-none rounded-lg border border-white/10 bg-white/[0.025] px-4 py-3 text-sm leading-relaxed text-white outline-none focus:border-[#FD954F]/70"
            />
            <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.025] p-4">
              <div className="mb-2 text-xs font-bold uppercase text-white/40">First greeting</div>
              <textarea
                value={draft.opening_message}
                onChange={(event) => updateDraft({ opening_message: event.target.value })}
                className="min-h-24 w-full resize-none bg-transparent text-sm leading-relaxed text-white outline-none"
              />
            </div>
          </div>

          <div className="border-t border-white/10 pt-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-bold text-white">Advanced Prompt</h3>
              <button
                type="button"
                onClick={() => setAdvancedOpen((value) => !value)}
                className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-3 py-1.5 text-xs font-bold text-white/65 hover:border-[#FD954F]/50 hover:text-white"
              >
                <PencilLine className="h-3.5 w-3.5" />
                {advancedOpen ? "Hide" : "Edit"}
              </button>
            </div>
            {advancedOpen ? (
              <textarea
                value={draft.system_prompt}
                onChange={(event) => updateDraft({ system_prompt: event.target.value })}
                className="min-h-72 w-full resize-none rounded-lg border border-white/10 bg-[#0F0F14] px-4 py-3 font-mono text-xs leading-relaxed text-white outline-none focus:border-[#FD954F]/70"
              />
            ) : (
              <p className="line-clamp-4 text-xs leading-relaxed text-white/45">
                {draft.system_prompt}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <PreviewExchange draft={draft} />
          <JourneyPreview
            enabled={builder.includeJourney}
            lessonCount={builder.lessonCount}
            items={journeyItems}
            loading={loadingJourney}
            onLessonCountChange={(lessonCount) => updateBuilder({ lessonCount })}
            onRegenerate={generateJourney}
          />
        </div>
      </div>
    );
  };

  return (
    <CinematicModal
      isOpen
      onClose={onClose}
      maxWidth="6xl"
      showCloseButton={false}
      titleId="create-tutor-modal-title"
      panelClassName="max-h-[calc(100dvh-2rem)]"
      contentClassName="flex min-h-0 flex-1 flex-col overflow-hidden p-0"
    >
      <div className="border-b border-white/10 bg-white/[0.03] px-5 py-4 sm:px-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-md border border-orange-400/25 bg-orange-500/10 px-3 py-1 text-xs font-bold text-orange-200">
              <Sparkles className="h-3.5 w-3.5" />
              Design your own tutor
            </div>
            <h2 id="create-tutor-modal-title" className="text-xl font-bold text-white sm:text-2xl">
              Build a tutor people can actually use
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={cinematicModalCloseButtonClass}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-5 grid grid-cols-4 gap-1.5 sm:gap-2">
          {STEPS.map((item, index) => {
            const status = getStepStatus(index, currentStep);
            return (
              <button
                key={item}
                type="button"
                onClick={() => setCurrentStep(index)}
                className={`min-w-0 rounded-lg border px-2 py-2 text-left transition sm:px-3 ${
                  status === "active"
                    ? "border-[#FD954F] bg-[#FD954F]/12"
                    : status === "complete"
                      ? "border-[#FD954F]/35 bg-[#FD954F]/5"
                      : "border-white/10 bg-white/[0.025]"
                }`}
              >
                <div className="text-[11px] font-black uppercase text-white/35">
                  {String(index + 1).padStart(2, "0")}
                </div>
                <div className="mt-1 truncate text-sm font-bold text-white">{item}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-7">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
          >
            {step === "Goal" && renderGoalStep()}
            {step === "Persona" && renderPersonaStep()}
            {step === "Session" && renderSessionStep()}
            {step === "Preview" && renderPreviewStep()}
          </motion.div>
        </AnimatePresence>
        {error && (
          <div className="mt-5 rounded-lg border border-red-400/30 bg-red-500/8 px-4 py-3 text-sm text-red-100">
            {error}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 bg-[#111216] px-5 py-4 sm:px-7">
        <button
          type="button"
          onClick={() => (currentStep > 0 ? setCurrentStep((prev) => prev - 1) : onClose())}
          className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-4 py-2.5 text-sm font-bold text-white/65 transition hover:bg-white/10 hover:text-white"
        >
          <ChevronLeft className="h-4 w-4" />
          {currentStep > 0 ? "Back" : "Cancel"}
        </button>

        <div className="flex items-center gap-3">
          {step === "Preview" && (
            <button
              type="button"
              onClick={generateDraft}
              disabled={loadingDraft || saving}
              className="hidden items-center gap-2 rounded-lg border border-white/10 px-4 py-2.5 text-sm font-bold text-white/70 transition hover:border-[#FD954F]/50 hover:text-white disabled:opacity-50 sm:inline-flex"
            >
              <RefreshCw className={`h-4 w-4 ${loadingDraft ? "animate-spin" : ""}`} />
              Regenerate
            </button>
          )}
          <button
            type="button"
            onClick={handleNext}
            disabled={!canContinue || loadingDraft || saving}
            className="inline-flex items-center gap-2 rounded-lg bg-[#FD954F] px-5 py-2.5 text-sm font-black text-[#181A22] transition hover:bg-[#ffae73] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loadingDraft || saving ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : step === "Preview" ? (
              <Save className="h-4 w-4" />
            ) : step === "Session" ? (
              <Eye className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {step === "Preview"
              ? "Save Tutor"
              : step === "Session"
                ? "Generate preview"
                : "Continue"}
          </button>
        </div>
      </div>
    </CinematicModal>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-2 flex items-end justify-between gap-3">
        <span className="text-sm font-bold text-white">{label}</span>
        {hint && <span className="text-right text-xs text-white/40">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function BuilderSelect<T extends string>({
  value,
  options,
  onValueChange,
  ariaLabel,
}: {
  value: T;
  options: T[];
  onValueChange: (value: T) => void;
  ariaLabel: string;
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className="flex h-12 w-full items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.04] px-4 text-left text-sm font-semibold text-white outline-none transition-colors hover:border-orange-300/35 hover:bg-white/[0.06] focus-visible:border-orange-300/60 focus-visible:ring-2 focus-visible:ring-orange-300/20"
        >
          <span className="truncate">{value}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-white/45" aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={8}
          collisionPadding={16}
          className="z-[10020] w-[var(--radix-dropdown-menu-trigger-width)] overflow-hidden rounded-lg border border-white/10 bg-[#15161B] p-2 text-sm text-white shadow-[0_22px_60px_rgba(0,0,0,0.55)]"
        >
          <DropdownMenu.RadioGroup
            value={value}
            onValueChange={(nextValue) => onValueChange(nextValue as T)}
          >
            <div className="max-h-72 overflow-y-auto">
              {options.map((option) => {
                const isSelected = option === value;
                return (
                  <DropdownMenu.RadioItem
                    key={option}
                    value={option}
                    className={`flex min-h-10 cursor-pointer items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm font-semibold outline-none transition-colors ${
                      isSelected
                        ? "bg-white text-black"
                        : "text-white/70 hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white"
                    }`}
                  >
                    <span className="truncate">{option}</span>
                    {isSelected && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
                  </DropdownMenu.RadioItem>
                );
              })}
            </div>
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function SegmentedExperience({
  value,
  onChange,
}: {
  value: TutorExperienceType;
  onChange: (value: TutorExperienceType) => void;
}) {
  return (
    <div>
      <div className="mb-3 text-sm font-bold text-white">Session surface</div>
      <div className="grid grid-cols-2 gap-2">
        {[
          { value: "conversation" as const, label: "Conversation", icon: MessageCircle },
          { value: "zen" as const, label: "Zen", icon: Sparkles },
        ].map((option) => {
          const Icon = option.icon;
          const selected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className={`rounded-lg border px-4 py-4 text-left transition ${
                selected
                  ? "border-[#FD954F] bg-[#FD954F]/12"
                  : "border-white/10 bg-white/[0.04] hover:bg-white/[0.07]"
              }`}
            >
              <Icon className={`mb-3 h-5 w-5 ${selected ? "text-[#FD954F]" : "text-white/50"}`} />
              <div className="text-sm font-bold text-white">{option.label}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function VoicePicker({
  selectedVoice,
  onSelect,
}: {
  selectedVoice: VoiceKey;
  onSelect: (voice: VoiceKey) => void;
}) {
  const SelectedIcon = VOICE_ICON_MAP[selectedVoice] || Sparkles;
  const renderItem = (voice: VoiceKey) => {
    const ItemIcon = VOICE_ICON_MAP[voice] || Sparkles;
    const isSelected = selectedVoice === voice;
    return (
      <DropdownMenu.RadioItem
        key={voice}
        value={voice}
        className={`cursor-pointer rounded-lg px-3 py-2 text-sm outline-none transition-colors ${
          isSelected
            ? "bg-white text-black"
            : "text-white/75 hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white"
        }`}
      >
        <span className="inline-flex items-center gap-3">
          <ItemIcon
            className={`h-5 w-5 ${isSelected ? "text-black/70" : "text-white/75"}`}
            aria-hidden="true"
          />
          <span className="flex flex-col text-left">
            <span>{VOICE_DISPLAY_NAMES[voice]}</span>
            <span
              className={`text-[11px] leading-tight ${
                isSelected ? "text-black/55" : "text-white/45"
              }`}
            >
              {VOICE_DESCRIPTIONS[voice]}
            </span>
          </span>
        </span>
      </DropdownMenu.RadioItem>
    );
  };

  return (
    <div>
      <div className="mb-3 text-sm font-bold text-white">Voice</div>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            aria-label="Choose tutor voice"
            className="flex h-12 w-full items-center justify-between rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-semibold text-white outline-none transition hover:border-[#FD954F]/40 hover:bg-[#1B1C22] focus-visible:border-orange-300/60 focus-visible:ring-2 focus-visible:ring-orange-300/20"
          >
            <span className="inline-flex items-center gap-2">
              <SelectedIcon className="h-5 w-5 text-[#FD954F]" aria-hidden="true" />
              {VOICE_DISPLAY_NAMES[selectedVoice]}
            </span>
            <ChevronDown className="h-4 w-4 text-white/45" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            sideOffset={8}
            align="start"
            collisionPadding={16}
            className="z-[10020] max-h-80 w-[var(--radix-dropdown-menu-trigger-width)] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-lg border border-white/10 bg-[#15161B] p-2 shadow-[0_22px_60px_rgba(0,0,0,0.55)]"
          >
            <DropdownMenu.RadioGroup
              value={selectedVoice}
              onValueChange={(voice) => onSelect(voice as VoiceKey)}
            >
              <div className="px-3 py-1 text-[11px] uppercase text-white/40">Free voices</div>
              {FREE_VOICES.map(renderItem)}
              <DropdownMenu.Separator className="my-2 h-px bg-white/10" />
              <div className="flex items-center gap-2 px-3 py-1 text-[11px] uppercase text-white/40">
                Premium voices
                <span className="inline-flex items-center gap-1 rounded-full bg-[#FD954F] px-1.5 py-0.5 text-[10px] font-black text-[#181A22]">
                  <Crown className="h-3 w-3" /> PRO
                </span>
              </div>
              {PREMIUM_VOICES.map(renderItem)}
            </DropdownMenu.RadioGroup>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}

function PreviewExchange({ draft }: { draft: GeneratedTutorDraft }) {
  return (
    <div className="border-t border-white/10 pt-5">
      <div className="mb-4 flex items-center gap-2">
        <Play className="h-4 w-4 text-[#FD954F]" />
        <h3 className="text-sm font-bold text-white">Sample exchange</h3>
      </div>
      <div className="space-y-3">
        {draft.sample_exchange.map((message, index) => (
          <div
            key={`${message.role}-${index}`}
            className={`rounded-lg px-4 py-3 text-sm leading-relaxed ${
              message.role === "tutor"
                ? "border border-[#FD954F]/20 bg-[#FD954F]/10 text-[#FFE1CD]"
                : "ml-8 border border-white/10 bg-white/[0.05] text-white/75"
            }`}
          >
            <div className="mb-1 text-[11px] font-black uppercase text-white/35">
              {message.role}
            </div>
            {message.text}
          </div>
        ))}
      </div>
    </div>
  );
}

function JourneyPreview({
  enabled,
  lessonCount,
  items,
  loading,
  onLessonCountChange,
  onRegenerate,
}: {
  enabled: boolean;
  lessonCount: number;
  items: TutorCurriculumDraftItem[];
  loading: boolean;
  onLessonCountChange: (count: number) => void;
  onRegenerate: () => void;
}) {
  return (
    <div className="border-t border-white/10 pt-5">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <div className="mb-1 flex items-center gap-2">
            <Route className="h-4 w-4 text-[#FD954F]" />
            <h3 className="text-sm font-bold text-white">Optional Journey</h3>
          </div>
          <p className="text-xs leading-relaxed text-white/45">
            These lessons only affect sessions launched from a Journey card.
          </p>
        </div>
        {enabled && (
          <button
            type="button"
            onClick={onRegenerate}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-white/65 transition hover:border-[#FD954F]/50 hover:text-white disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        )}
      </div>

      {!enabled ? (
        <div className="rounded-lg border border-white/10 bg-white/[0.025] px-4 py-4 text-sm text-white/50">
          Journey generation is off. This tutor will still work as a freeform session.
        </div>
      ) : (
        <>
          <div className="mb-4 flex items-center gap-3">
            <span className="text-xs font-bold uppercase text-white/35">Lessons</span>
            <input
              type="range"
              min={3}
              max={7}
              value={lessonCount}
              onChange={(event) => onLessonCountChange(Number(event.target.value))}
              className="accent-[#FD954F]"
            />
            <span className="text-sm font-black text-white">{lessonCount}</span>
          </div>
          <div className="space-y-3">
            {items.length === 0 ? (
              <div className="rounded-lg border border-white/10 bg-white/[0.025] px-4 py-4 text-sm text-white/50">
                Generate the preview to create editable lesson cards.
              </div>
            ) : (
              items.map((item) => (
                <div
                  key={item.position}
                  className="rounded-lg border border-white/10 bg-white/[0.025] p-4"
                >
                  <div className="mb-2 flex items-center gap-2">
                    <span className="rounded-full bg-[#FD954F] px-2 py-0.5 text-xs font-black text-[#181A22]">
                      {item.position}
                    </span>
                    <span className="text-sm font-bold text-white">{item.title}</span>
                  </div>
                  <p className="text-xs leading-relaxed text-white/50">{item.objective}</p>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
